import { useQuery } from '@apollo/client';
import { ApolloLogo } from '@apollo/space-kit/icons/ApolloLogo';
import classnames from 'classnames';
import React from 'react';

import * as orgRouteConfig from 'src/app/account/routes';
import { SudoToggle } from 'src/app/appLayout/components/globalHeader/sudoToggle/SudoToggle';
import { OutlinkForEmbed } from 'src/app/embedHelpers/OutlinkForEmbed';
import { orgSwitcherQuery } from 'src/components/appNav/orgSwitcher/OrgSwitcher';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import {
  OrgSwitcherQuery,
  OrgSwitcherQueryVariables,
} from 'src/lib/graphqlTypes/types';

import { SettingsMenu } from './SettingsMenu';

interface Props {
  isExpanded: boolean;
}

export const SidebarHeader = ({ isExpanded }: Props) => {
  const [currentAccountId] = useCurrentAccountId();

  const { data, loading } = useQuery<
    OrgSwitcherQuery,
    OrgSwitcherQueryVariables
  >(orgSwitcherQuery);

  if (loading || !data?.me || data?.me.__typename !== 'User') return null;

  const me = data.me;
  const org = [...data.me.memberships].find(
    (m) => m.account.id === currentAccountId,
  )?.account;
  const orgAvatarUrl = org?.avatarUrl || '';
  const isSSO = data.me.type === 'SSO';
  const isSuperUser = !org && data.me.internalAdminRole;

  return (
    <header className="flex min-h-16 flex-col gap-2 bg-secondary">
      <div
        className={classnames(
          'flex size-full min-h-16 flex-row gap-2 border-b border-primary p-4',
          isExpanded ? 'sm:flex-row ' : 'sm:flex-col',
        )}
      >
        <OutlinkForEmbed
          aria-label="Apollo Home"
          to={
            currentAccountId
              ? orgRouteConfig.org.path({ orgId: currentAccountId })
              : '/'
          }
          className={classnames(
            'mr-auto flex items-center gap-2 justify-self-start rounded-full bg-info p-2 text-sm text-primary [&_path:last-of-type]:hover:origin-center [&_path:last-of-type]:hover:animate-custom-spin',
            isExpanded ? 'sm:mx-0' : 'sm:mx-auto',
          )}
        >
          <ApolloLogo className="size-5" />
          <span
            className={classnames(
              'transition-all',
              isExpanded ? 'visible' : 'hidden',
            )}
          >
            STUDIO
          </span>
        </OutlinkForEmbed>
        {isExpanded ? <div className="w-full" /> : null}
        {me?.internalAdminRole && (
          <div className="my-auto ml-auto sm:m-auto">
            <SudoToggle
              adminRole={me.internalAdminRole}
              keepCompact={!isExpanded}
            />
          </div>
        )}
        {!loading ? (
          <SettingsMenu
            className="justify-self-end focus:ring-2"
            orgName={org?.name}
            orgPlanName={org?.currentPlan?.name}
            orgAvatarUrl={orgAvatarUrl}
            userAvatarUrl={me?.avatarUrl || ''}
            userFullName={me?.fullName}
            userEmail={me?.email || ''}
            currentAccountId={currentAccountId || ''}
            canCreateOrg={!isSSO && !isSuperUser}
            orgMemberships={me?.memberships}
          />
        ) : null}
      </div>
    </header>
  );
};
